import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { Brain, Star, TrendingUp, TrendingDown, HelpCircle, AlertTriangle, Loader2, ArrowUp, ArrowDown, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const CLASS_CONFIG = {
  star: { label: "Estrela", emoji: "star", color: "text-amber-600", bg: "bg-amber-50 border-amber-200", desc: "Popular + Lucrativo" },
  plow_horse: { label: "Cavalo de Trabalho", emoji: "horse", color: "text-blue-600", bg: "bg-blue-50 border-blue-200", desc: "Popular, baixa margem" },
  puzzle: { label: "Quebra-cabeca", emoji: "puzzle", color: "text-purple-600", bg: "bg-purple-50 border-purple-200", desc: "Lucrativo, pouco vendido" },
  dog: { label: "Revisar", emoji: "dog", color: "text-red-600", bg: "bg-red-50 border-red-200", desc: "Baixa popularidade e margem" },
};

export default function AIMenuEngineeringPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");
  const [filter, setFilter] = useState("all");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxiosRef.current.get(`/ai-menu-engineering/analysis?days=${period}`);
      setData(res.data);
    } catch { toast.error("Erro ao carregar analise"); }
    setLoading(false);
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <MainLayout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div></MainLayout>;
  if (!data || data.items.length === 0) return (
    <MainLayout>
    <div className="text-center py-20">
      <Brain className="w-12 h-12 text-gray-300 mx-auto mb-4" />
      <p className="text-lg font-bold text-gray-500">Sem dados suficientes</p>
      <p className="text-sm text-muted-foreground">Precisa de pedidos para gerar a analise de cardapio.</p>
    </div>
    </MainLayout>
  );

  const filteredItems = filter === "all" ? data.items : data.items.filter(i => i.classification === filter);
  const s = data.summary;

  return (
    <MainLayout>
    <div className="space-y-6 max-w-5xl" data-testid="menu-engineering-page">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2"><Brain className="w-5 h-5 text-emerald-600" /> Menu Engineering com IA</h2>
        <p className="text-sm text-muted-foreground mt-1">Analise de popularidade x lucratividade de cada prato. Baseado em dados reais dos ultimos {period} dias.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Receita total</p>
          <p className="text-xl font-black">{s.total_revenue.toFixed(0)} EUR</p>
          <p className="text-[10px] text-muted-foreground">{s.total_orders} itens vendidos</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 cursor-pointer hover:shadow-sm" onClick={() => setFilter(filter === "star" ? "all" : "star")} data-testid="filter-star">
          <p className="text-xs text-amber-700 font-bold">Estrelas</p>
          <p className="text-2xl font-black text-amber-600">{s.stars}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 cursor-pointer hover:shadow-sm" onClick={() => setFilter(filter === "plow_horse" ? "all" : "plow_horse")} data-testid="filter-plow">
          <p className="text-xs text-blue-700 font-bold">Cavalos de Trabalho</p>
          <p className="text-2xl font-black text-blue-600">{s.plow_horses}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 cursor-pointer hover:shadow-sm" onClick={() => setFilter(filter === "puzzle" ? "all" : "puzzle")} data-testid="filter-puzzle">
          <p className="text-xs text-purple-700 font-bold">Quebra-cabecas</p>
          <p className="text-2xl font-black text-purple-600">{s.puzzles}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 cursor-pointer hover:shadow-sm" onClick={() => setFilter(filter === "dog" ? "all" : "dog")} data-testid="filter-dog">
          <p className="text-xs text-red-700 font-bold">Revisar</p>
          <p className="text-2xl font-black text-red-600">{s.dogs}</p>
        </div>
      </div>

      {/* Period selector */}
      <div className="flex items-center gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Ultimos 7 dias</SelectItem>
            <SelectItem value="30">Ultimos 30 dias</SelectItem>
            <SelectItem value="90">Ultimos 90 dias</SelectItem>
          </SelectContent>
        </Select>
        {filter !== "all" && (
          <Button variant="outline" size="sm" onClick={() => setFilter("all")}>Limpar filtro</Button>
        )}
      </div>

      {/* Suggestions */}
      {data.suggestions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h3 className="font-bold flex items-center gap-2 mb-3"><Lightbulb className="w-4 h-4 text-amber-600" /> Sugestoes da IA ({data.suggestions.length})</h3>
          <div className="space-y-2">
            {data.suggestions.slice(0, 5).map((sug, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-3 border" data-testid={`suggestion-${i}`}>
                <div className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center ${sug.priority === "high" ? "bg-red-100" : "bg-amber-100"}`}>
                  {sug.type === "price_increase" ? <ArrowUp className="w-3 h-3 text-red-600" /> : sug.type === "promote" ? <TrendingUp className="w-3 h-3 text-purple-600" /> : <AlertTriangle className="w-3 h-3 text-amber-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm">{sug.message}</p>
                  {sug.type === "price_increase" && (
                    <p className="text-xs text-emerald-600 font-bold mt-1">{sug.current_value?.toFixed(2)} EUR → {sug.suggested_value?.toFixed(2)} EUR</p>
                  )}
                </div>
                <span className={`text-[9px] font-bold px-2 py-1 rounded-full shrink-0 ${sug.priority === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{sug.priority.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left text-xs font-bold text-gray-500 px-4 py-3">Produto</th>
                <th className="text-center text-xs font-bold text-gray-500 px-3 py-3">Class.</th>
                <th className="text-right text-xs font-bold text-gray-500 px-3 py-3">Preco</th>
                <th className="text-right text-xs font-bold text-gray-500 px-3 py-3">Margem</th>
                <th className="text-right text-xs font-bold text-gray-500 px-3 py-3">Qtd vendida</th>
                <th className="text-right text-xs font-bold text-gray-500 px-3 py-3">Receita</th>
                <th className="text-right text-xs font-bold text-gray-500 px-3 py-3">% Receita</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => {
                const cls = CLASS_CONFIG[item.classification] || CLASS_CONFIG.dog;
                return (
                  <tr key={item.product_id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`menu-item-${item.product_id}`}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium">{item.product_name}</p>
                      <p className="text-[10px] text-muted-foreground">{item.category}</p>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${cls.bg}`}>{cls.label}</span>
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-medium">{item.price.toFixed(2)}</td>
                    <td className="px-3 py-3 text-right">
                      <span className={`text-sm font-bold ${item.margin_pct >= 50 ? "text-emerald-600" : "text-red-500"}`}>{item.margin_pct.toFixed(0)}%</span>
                    </td>
                    <td className="px-3 py-3 text-right text-sm">{item.total_quantity}</td>
                    <td className="px-3 py-3 text-right text-sm font-medium">{item.total_revenue.toFixed(2)}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(item.revenue_share, 100)}%` }} /></div>
                        <span className="text-[10px] text-muted-foreground w-8 text-right">{item.revenue_share}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </MainLayout>
  );
}
